<template>
  <v-wait class="subscribe-button__wrapper" for="BillingSubscribe">
    <template v-slot:waiting>
      <AppButton
        class="subscribe-button"
        :class="buttonVariantClass"
        :outlined="useButtonOutlinedStyle"
        disabled
      >
        Subscribing <button-spinner />
      </AppButton>
    </template>
    <AppButton
      class="subscribe-button"
      :class="buttonVariantClass"
      :outlined="useButtonOutlinedStyle"
      @click="subscribe"
    >
      {{ buttonText }}
    </AppButton>
  </v-wait>

  <div v-if="errorRef !== null" class="plan-card-error">
    Error subscribing<span v-if="errorRef !== ''">: {{ errorRef }}</span>
  </div>

  <BillingApplePriceComparison16218
    v-if="billing.showExternalPayment16218"
    ref="iosComparisonPopup"
    :billing="billing as AppStoreBillingView"
    :plan="plan"
    @appStoreSubscribe="appStoreSubscribe"
  />
  <SubscribePopup v-else ref="popup" :billing="billing" :plan="plan" />
</template>

<script setup lang="ts">
import { computed, ref, onBeforeMount } from 'vue'
import { useWait } from '@/app/composables/useWait'
import { assertNotNull } from '@/helpers/typing'

import type {
  AppStoreBillingView,
  BillingView,
  PlanView,
} from '@/models/billing'
import { platform } from '@/services/platform'
import {
  GoogleBillingUnavailableError,
  UserCancelledError,
} from '@/services/cordova.purchase.types'
import { ValidationError } from '@/models/error'

import AppButton from '@/app/components/button/AppButton.vue'
import SubscribePopup from './SubscribePopup.vue'
import BillingApplePriceComparison16218 from './BillingApplePriceComparison16218.vue'

import { Mixpanel } from '@/analytics/mixpanel'
import { trialByPlanV2_18369Treatment } from '@/services/ab/18369_trial_by_plan'

const props = defineProps<{
  billing: BillingView
  plan: PlanView
  location?: string
  /** Overrides the button's default label logic when set. */
  label?: string
}>()

const wait = useWait()

const errorRef = ref<string | null>(null)
const popup = ref<InstanceType<typeof SubscribePopup> | null>(null)
const iosComparisonPopup = ref<InstanceType<
  typeof BillingApplePriceComparison16218
> | null>(null)

const useButtonOutlinedStyle = props.plan.id === 'plan_monthly'
const buttonVariantClass = `subscribe-button--${props.plan.id}`
const buttonText = computed(() => {
  if (props.label) {
    return props.label
  }

  if (props.billing.isTrialOver) {
    return 'Subscribe'
  }

  if (trialByPlanV2_18369Treatment()) {
    return props.plan.id === 'plan_annual'
      ? 'Start 14-day Free Trial'
      : 'Select Plan'
  }

  return 'Start Free Trial'
})

const isAndroidApp = ref<boolean>(false)
onBeforeMount(async () => {
  isAndroidApp.value = await platform.isNativeAndroidApp()
})

async function subscribe(): Promise<void> {
  Mixpanel.trackStartTrialButton(props.plan, props.location || '')

  if (!props.billing.isAppStore()) {
    return assertNotNull(popup.value).show()
  }

  if (props.billing.showExternalPayment16218) {
    return assertNotNull(iosComparisonPopup.value).show()
  }

  await appStoreSubscribe()
}

async function appStoreSubscribe(): Promise<void> {
  try {
    errorRef.value = null
    wait.start('BillingSubscribe')
    await props.billing.subscribeTo(props.plan)
    wait.end('BillingSubscribe')
  } catch (error) {
    if (error instanceof UserCancelledError) {
      errorRef.value = 'cancelled'
    } else if (error instanceof ValidationError) {
      errorRef.value = error.getOne('receipt')
    } else if (error instanceof GoogleBillingUnavailableError) {
      errorRef.value =
        "We couldn't initiate your purchase. Please ensure that you are logged into your Google account on your device, and you have installed the latest version of Google Play. Alternatively, you can subscribe at https://www.shortform.com/app/billing in a web browser."
    } else if ('message' in (error as any)) {
      errorRef.value = (error as any).message
    } else {
      errorRef.value = ''
    }
    wait.end('BillingSubscribe')
  }
  iosComparisonPopup.value?.close()
}
</script>

<style lang="scss" scoped>
.subscribe-button {
  width: 100%;
  padding: 9px 19px;
  font-size: 20px;
  font-weight: 500;
  line-height: 24px;
  @media only screen and (max-width: $w-tablet-s) {
    font-size: 18px;
    line-height: 22px;
  }
}

.subscribe-button--plan_annual {
  border: 1px solid transparent;
}

.plan-card-error {
  font-size: 16px;
  text-align: center;
  color: $c-red;
}

.subscription-info__text {
  margin-bottom: 5px;
}
</style>
